import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import { sum } from 'lib/array/arrayUtils'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { buildAvailableRateKey } from 'lib/offer/availabilityUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { getCalenderDaysByCheckIn } from 'lib/offer/offerCalendarUtils'
import { getPropertyRatePricingItemTotals, isPropertyRateLoyaltyPackageUpgradeItem } from 'checkout/lib/utils/loyalty/propertyRateLoyaltyUpgrade'
import { getExtraGuestSurchargePayableAtProperty } from 'checkout/lib/utils/accommodation/extraGuestSurchargeSplit'
import { applyCancellationOptionPriceFactor } from 'lib/offer/cancellationOptionPricing'

export function getInstantBookingPriceAndSurcharge(
  item: App.Checkout.InstantBookingLEHotelItem,
  offer: App.Offer,
  calendarsRatesByOccupancy: { [occupancyKey: string]: { [packageKey: string]: App.Calendar } },
  availableRatesByOccupancy: { [offerId: string]: App.OfferAvailableRatesByOccupancy },
  options: {
    calendarsByOccupancyLereGroups?: App.CalendarState['calendarsByOccupancyLereGroups']
    offerAvailableRatesLereGroups?: App.OfferState['offerAvailableRatesLereGroups']
    lereGroup?: string
  } = {},
): App.WithDataStatus<App.Checkout.ItemViewTotals & { extraGuestSurchargePayableAtProperty: number }> {
  let hasRequiredData = false
  let surcharge = 0
  let extraGuestSurcharge = 0
  let extraGuestSurchargePayableAtProperty = 0
  let price = 0
  let memberPrice = 0
  let taxesAndFees = 0
  let dynamicPricingSurchargeIncludedInSource = false
  // Un-marked base rates (before channel markup). For LPC these are sent to
  // svc-reservation as `room_rate_amount`, which strict-equality validates the base.
  let baseRoomRate = 0
  let baseMemberRoomRate = 0

  const propertyRateUpgrade = item.loyaltyPropertyRateUpgrade
  if (propertyRateUpgrade) {
    const pricing = isPropertyRateLoyaltyPackageUpgradeItem(item) ? propertyRateUpgrade.targetPricing : propertyRateUpgrade.sourcePricing
    if (pricing) {
      return {
        hasRequiredData: true,
        data: {
          ...getPropertyRatePricingItemTotals(pricing),
          extraGuestSurchargePayableAtProperty: 0,
        },
      }
    }
  }

  let uniqueKey: string = getPackageUniqueKey(item.packageId, item.duration, item.roomRateId, item.cancellationPolicy)

  if (item.loyaltyAppliedUpgradeType && !isPropertyRateLoyaltyPackageUpgradeItem(item)) {
    const originPackage = offer.packages.find(pkg => pkg.uniqueKey === uniqueKey)
    const targetUpgradePackageUniqueKey = originPackage?.loyalty.upgradeTargetsUniqueKeys?.[item.loyaltyAppliedUpgradeType]
    if (targetUpgradePackageUniqueKey) {
      uniqueKey = targetUpgradePackageUniqueKey
    }
  }

  if (offer.type === OFFER_TYPE_ALWAYS_ON && (offer.property?.useDynamicOccupancy || offer.property?.useDynamicCancellationPolicies)) {
    const key = buildAvailableRateKey(item.checkIn, item.checkOut, [item.occupancy])
    const availableRates = availableRatesByOccupancy[item.offerId]?.[key]?.rates
    const availableRate = availableRates?.find(rate => rate.packageUniqueKey === uniqueKey)
    const availableRatesLereGroup = options.offerAvailableRatesLereGroups?.[item.offerId]?.[key]

    if (availableRate) {
      hasRequiredData = true
      dynamicPricingSurchargeIncludedInSource = !!options.lereGroup && availableRatesLereGroup === options.lereGroup
      surcharge = availableRate.surcharge
      extraGuestSurcharge = availableRate.extraGuestSurcharge
      price = availableRate.hotelPrice
      memberPrice = availableRate.hotelMemberPrice
      taxesAndFees = availableRate.taxesAndFees
      baseRoomRate = availableRate.roomPrices ? sum(availableRate.roomPrices) : availableRate.hotelPrice
      baseMemberRoomRate = availableRate.roomMemberPrices ? sum(availableRate.roomMemberPrices) : availableRate.hotelMemberPrice
    }
  } else {
    const occupancyKey = generateOccupancyStringByRoom(item.occupancy)
    const calendars = calendarsRatesByOccupancy[occupancyKey]
    const pkgCalendar = calendars?.[uniqueKey]
    const pkgByDay = pkgCalendar && getCalenderDaysByCheckIn(pkgCalendar)[item.checkIn]
    const pkg = offer.packages.find(p => p.uniqueKey === uniqueKey)
    const calendarLereGroup = options.calendarsByOccupancyLereGroups?.[occupancyKey]?.[uniqueKey]

    if (pkgByDay) {
      hasRequiredData = true
      dynamicPricingSurchargeIncludedInSource = !!options.lereGroup && calendarLereGroup === options.lereGroup
      surcharge = pkgByDay.surcharge
      extraGuestSurcharge = pkgByDay.extraGuestSurcharge ?? 0
      extraGuestSurchargePayableAtProperty = getExtraGuestSurchargePayableAtProperty(pkgByDay, pkg?.roomRate)
      price = pkgByDay.hotelPrice
      memberPrice = pkgByDay.hotelMemberPrice
      taxesAndFees = pkgByDay.taxesAndFees
      baseRoomRate = pkgByDay.roomPrices ? sum(pkgByDay.roomPrices) : pkgByDay.hotelPrice
      baseMemberRoomRate = pkgByDay.roomMemberPrices ? sum(pkgByDay.roomMemberPrices) : pkgByDay.hotelMemberPrice
    }
  }

  const factor = item.cancellationOptionPriceFactor
  return {
    hasRequiredData,
    data: {
      surcharge,
      extraGuestSurcharge,
      extraGuestSurchargePayableAtProperty,
      price: applyCancellationOptionPriceFactor(price, factor),
      memberPrice: applyCancellationOptionPriceFactor(memberPrice, factor),
      baseRoomRate: applyCancellationOptionPriceFactor(baseRoomRate, factor),
      baseMemberRoomRate: applyCancellationOptionPriceFactor(baseMemberRoomRate, factor),
      taxesAndFees,
      value: 0,
      memberValue: 0,
      dynamicPricingSurchargeIncludedInSource,
    },
  }
}
